/** The itinerary outline (plan §5): 290 px, the days and their stops in time order — the
 *  records outline (`RecordOutline`) with the tour's rows, words and moves.
 *
 *  - One tab stop (a tree with roving focus): ↑ / ↓ (Home / End) move between rows, Enter
 *    opens a row in the inspector. In edit mode Alt+↑ / Alt+↓ move a day, Delete removes
 *    the row, Shift+F10 opens its actions; the drag handles (`@dnd-kit`, pointer or
 *    keyboard) move a day, or drop a stop on another day.
 *  - Its rows come from one derived value of the draft (the workspace's `useDraftSelector`):
 *    typing in a stop renders that stop's row only; the outline renders when a row
 *    appears, goes or moves.
 *  - Above 150 rows it is virtualised (`@tanstack/react-virtual`): only the rows in view
 *    (and a few around them) are mounted.
 *  - "Add day" sits at its foot in edit mode; its room is kept in view mode. */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { APPBAR_PX, DOCK_GAP_PX, SCROLLER_PADDING_PX } from '@/features/records/page/recordPage';

import { useSnackbar } from '@/components/Snackbar';
import { useDraftStable } from '@/features/records/draft/DraftProvider';
import { RecordOutline, type OutlineDrag, type OutlineSelection } from '@/features/records/outline/Outline';
import type { OutlineRowProps } from '@/features/records/outline/OutlineRowShell';

import { useTourRecord } from '../record/TourRecordContext';

import { DRAG_INSTRUCTIONS, dragAnnouncement, dropOutcome, type DragNames } from './drag';
import { dayRec, hhmm, listIn, slotRec, valueIn, DAYS_PATH } from './model';
import type { OutlineRow } from './outlineRows';
import { DAY_ROW_PX, OutlineDayRow, OutlineSlotRow, SLOT_ROW_PX } from './OutlineRowItems';
import { useItinerary } from './store';
import { addDay, dayName, moveDay, moveSlotToDay, removeDay, removeSlot, slotName } from './structure';
import { useStructure } from './useStructure';

export { VIRTUALIZE_ABOVE } from '@/features/records/outline/Outline';

export const OUTLINE_WIDTH = 290;
/** Sticky under the page's chrome, as the docked Changes column is: stuck 12 px below the
 *  scroll container's top, and as tall as the viewport allows (16 px above its bottom) —
 *  the next stop is always in reach without scrolling back up. */
export const OUTLINE_STICKY_TOP = DOCK_GAP_PX - SCROLLER_PADDING_PX;
export const OUTLINE_MAX_HEIGHT = `calc(100vh - ${APPBAR_PX + SCROLLER_PADDING_PX + OUTLINE_STICKY_TOP + 16}px)`;
const ADD_ROW_PX = 40;

export type Selection = OutlineSelection<OutlineRow['kind']>;

export interface OutlineProps {
  /** The rows (`outlineRows` over the draft — the workspace derives them once). */
  rows: readonly OutlineRow[];
  selection: Selection;
  onSelect: (selection: Selection) => void;
}

const rowHeight = (r: OutlineRow) => (r.kind === 'day' ? DAY_ROW_PX : SLOT_ROW_PX);

const LABELS = {
  nav: 'Itinerary outline',
  tree: 'Days and stops',
  help: 'Arrow keys move between rows and Enter opens one.',
  liveHelp: 'Alt with an arrow moves a day, Delete removes a row, Shift+F10 opens its actions.',
  empty: 'No days yet.',
  liveEmpty: 'Add the first day below.',
};

export function Outline({ rows, selection, onSelect }: OutlineProps) {
  const { editing, canEdit, baseline, tourRec } = useItinerary();
  const live = editing && canEdit;
  const run = useStructure();
  const dayCount = useMemo(() => rows.filter((r) => r.kind === 'day' && !r.removed).length, [rows]);

  const onCreated = useCallback(
    (kind: 'day' | 'slot', id: number) => {
      onSelect({ kind, id });
    },
    [onSelect],
  );

  // ---- keyboard edits ----------------------------------------------------------------
  const keyMove = useMemo(
    () => ({
      accepts: (row: OutlineRow) => row.kind === 'day' && row.index !== null,
      move: (row: OutlineRow, delta: -1 | 1) => row.kind === 'day' && row.index !== null && run((c) => moveDay(c, row.id, row.index! + delta)) !== null,
    }),
    [run],
  );
  const onRemoveKey = useCallback(
    (row: OutlineRow) => run((c) => (row.kind === 'day' ? removeDay(c, row.id) : removeSlot(c, row.id))) !== null,
    [run],
  );

  // ---- drag and drop (pointer; the keyboard path is Alt+arrows and the menus) --------
  const { store } = useDraftStable();
  const { names } = useTourRecord();
  const snackbar = useSnackbar();
  /** Rows by name, read from the draft as it stands (announcements, the overlay). */
  const dragNames = useMemo<DragNames>(
    () => ({
      nameOf: (r) => {
        const state = store.getState();
        const ctx = { state, baselines: baseline.paths, tourRec, names };
        if (r.kind === 'day') {
          const n = listIn(editing ? state.entries : null, baseline.paths, tourRec, DAYS_PATH).indexOf(r.id) + 1;
          if (n === 0) return dayName(ctx, r.id); // a removed day: its title
          const t = valueIn(editing ? state.entries : null, baseline.paths, dayRec(r.id), 'day.title');
          return `Day ${n}${typeof t === 'string' && t.trim() ? ` · ${t.trim()}` : ''}`;
        }
        const time = valueIn(editing ? state.entries : null, baseline.paths, slotRec(r.id), 'slot.time');
        return `${typeof time === 'string' && time ? `${hhmm(time)} ` : ''}${slotName(ctx, r.id)}`;
      },
      dayNumberOf: (dayId) => listIn(editing ? store.getState().entries : null, baseline.paths, tourRec, DAYS_PATH).indexOf(dayId) + 1,
    }),
    [store, baseline, tourRec, names, editing],
  );
  const drag = useMemo<OutlineDrag<OutlineRow>>(
    () => ({
      instructions: DRAG_INSTRUCTIONS,
      announce: (phase, from, over, all) => dragAnnouncement(phase, from, over, all, dragNames),
      onDrop: (from, over, all) => {
        const outcome = dropOutcome(from, over, all);
        if (!outcome) return;
        if (outcome.kind === 'move-day') run((c) => moveDay(c, outcome.dayId, outcome.toIndex));
        else if (outcome.kind === 'move-slot') run((c) => moveSlotToDay(c, outcome.slotId, outcome.toDayId));
        else {
          snackbar.show(
            `${dragNames.nameOf(from!)} stays in Day ${dragNames.dayNumberOf(outcome.dayId)}: stops are in time order — change its time to move it within the day, or drop it on another day.`,
            'info',
          );
        }
      },
      overlay: (row) => ({ name: dragNames.nameOf(row), strong: row.kind === 'day' }),
    }),
    [dragNames, run, snackbar],
  );

  const renderRow = (r: OutlineRow, props: OutlineRowProps) => {
    const rowProps = { ...props, row: r, onCreated, dayCount };
    return r.kind === 'day' ? <OutlineDayRow key={`d${r.id}`} {...rowProps} /> : <OutlineSlotRow key={`s${r.id}`} {...rowProps} />;
  };

  return (
    <RecordOutline
      rows={rows}
      selection={selection}
      onSelect={onSelect}
      rowHeight={rowHeight}
      renderRow={renderRow}
      live={live}
      keyMove={keyMove}
      onRemoveKey={onRemoveKey}
      drag={drag}
      labels={LABELS}
      width={OUTLINE_WIDTH}
      sx={{
        flex: 'none',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        position: 'sticky',
        top: OUTLINE_STICKY_TOP,
        maxHeight: OUTLINE_MAX_HEIGHT,
        alignSelf: 'flex-start',
      }}
      footer={
        // Reserved in both modes: pressing E adds no row.
        <Box data-itin-add="day" sx={{ height: ADD_ROW_PX, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', px: 1, flex: 'none' }}>
          {live && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                const id = run(addDay);
                if (typeof id === 'number') onSelect({ kind: 'day', id });
              }}
            >
              Add day
            </Button>
          )}
        </Box>
      }
    />
  );
}
